import React from 'react';
import { Bot, Mail, Lock, Eye, EyeOff, BarChartHorizontalBig, Zap, BrainCircuit } from 'lucide-react';

// Standalone component for the left-hand side descriptive content
const LandingPageInfo = () => (
  <div className="w-full lg:w-1/2 p-8 lg:p-12 bg-gradient-to-br from-blue-600 to-blue-800 text-white flex flex-col justify-center rounded-l-lg">
    <div className="mb-8">
      <Bot size={48} className="mb-4" />
      <h1 className="text-4xl font-bold mb-4">Welcome to Fortusight.ai</h1>
      <p className="text-lg text-blue-100 mb-6">
        Unlock the power of AI for your financial insights and portfolio management. 
        Fortusight.ai provides cutting-edge tools to help you make smarter decisions, faster.
      </p>
    </div>
    
    <div className="space-y-6">
      <div className="flex items-start">
        <BarChartHorizontalBig size={24} className="mr-3 mt-1 text-blue-300 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-xl">Advanced Analytics</h3>
          <p className="text-blue-200 text-sm">Leverage sophisticated models and real-time data for comprehensive market analysis and stock screening.</p>
        </div>
      </div>
      <div className="flex items-start">
        <Zap size={24} className="mr-3 mt-1 text-blue-300 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-xl">AI-Powered Insights</h3>
          <p className="text-blue-200 text-sm">Gain a competitive edge with AI-driven predictions, sentiment analysis, and personalized recommendations.</p>
        </div>
      </div>
      <div className="flex items-start">
        <BrainCircuit size={24} className="mr-3 mt-1 text-blue-300 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-xl">Intuitive Portfolio Management</h3>
          <p className="text-blue-200 text-sm">Seamlessly manage your assets, track performance, and explore new opportunities with our user-friendly interface.</p>
        </div>
      </div>
    </div>
    <p className="mt-10 text-center text-sm text-blue-200">
        Join thousands of users leveraging AI for financial success.
    </p>
  </div>
);

// Standalone component for the actual form fields and logic
const AuthFormFields = ({
  authMode, // Still needed for some conditional text/styling within the form part
  handleLogin,
  handleSignup,
  authError,
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  isAuthProcessing,
  handleGoogleSignIn,
  toggleAuthMode
}) => (
  <div className={`w-full p-6 sm:p-8 lg:p-12 ${authMode === 'signup' ? 'lg:w-1/2' : 'max-w-md mx-auto'} bg-white flex flex-col justify-center ${authMode === 'signup' ? 'rounded-r-lg' : 'rounded-lg shadow-xl' }`}>
    <div className={`flex justify-center mb-6 ${authMode === 'signup' ? 'lg:hidden' : ''}`}> 
      <div className="flex items-center">
        <Bot className="text-blue-600 mr-2" size={24} />
        <span className="font-bold text-xl">Fortusight.ai</span>
      </div>
    </div>
    
    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6">
      {authMode === 'login' ? 'Log in to your account' : 'Create your Fortusight.ai Account'}
    </h2>
    
    {authError && (
      <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
        {authError}
      </div>
    )}
    
    <form onSubmit={authMode === 'login' ? handleLogin : handleSignup} className="space-y-5">
      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1.5" htmlFor="email">
          Email
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            id="email"
            type="email"
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
      </div>
      
      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1.5" htmlFor="password">
          Password
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      
      <button
        type="submit"
        className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        disabled={isAuthProcessing}
      >
        {isAuthProcessing ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
        ) : (
          authMode === 'login' ? 'Log in' : 'Sign up'
        )}
      </button>
    </form>
    
    <div className="mt-5">
      <div className="relative flex items-center">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="flex-shrink mx-4 text-gray-600 text-sm">or continue with</span>
        <div className="flex-grow border-t border-gray-300"></div>
      </div>
      
      <button
        type="button"
        onClick={handleGoogleSignIn}
        className="w-full mt-4 border border-gray-300 bg-white text-gray-700 py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1"
        disabled={isAuthProcessing}
      >
        <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Google
      </button>
    </div>
    
    <div className="mt-6 text-center text-sm">
      {authMode === 'login' ? (
        <p className="text-gray-600">
          Don't have an account?{' '}
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 font-medium"
            onClick={toggleAuthMode}
          >
            Create account
          </button>
        </p>
      ) : (
        <p className="text-gray-600">
          Already have an account?{' '}
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 font-medium"
            onClick={toggleAuthMode}
          >
            Log in
          </button>
        </p>
      )}
    </div>
  </div>
);

// Main AuthForm component that decides the layout
const AuthForm = (props) => {
  if (props.authMode === 'signup') {
    return (
      <div className="w-full max-w-5xl mx-auto flex flex-col lg:flex-row shadow-xl rounded-lg overflow-hidden">
        <LandingPageInfo />
        <AuthFormFields {...props} />
      </div>
    );
  }

  // Login mode (centered form)
  // For login, we directly render AuthFormFields which now handles its own centering (max-w-md mx-auto) 
  // and applies shadow and rounded corners itself if not part of the two-column layout.
  return <AuthFormFields {...props} />;
};

export default AuthForm; 